import {ErrorHandler, Injectable} from '@angular/core';
import * as LogRocket from 'logrocket';
import { environment } from '../../environments/environment';

/**
 * Global error handler.
 *
 * Special-cases Angular's NG02100 (InvalidPipeArgument) as a runtime safety net.
 * We standardise on the crash-proof `safeDate`/`safeCurrency`/`safeNumber` pipes
 * (enforced by `npm run lint:pipes`), but this handler is defence-in-depth: if a
 * raw pipe ever slips through — or an NG02100 comes from a pipe we didn't wrap
 * (titlecase/percent/slice/third-party) — the error is de-duplicated so a single
 * bad value re-rendered on every change-detection tick can't flood the console or
 * LogRocket. Every other error keeps its original behaviour unchanged.
 */
@Injectable()
export class CustomErrorHandlerService extends ErrorHandler {

  /** NG02100 signatures already reported this session (dedup to stop floods). */
  private readonly reportedPipeErrors = new Set<string>();

  /** Cap on distinct NG02100 signatures captured to LogRocket per session. */
  private static readonly MAX_PIPE_ERRORS = 20;

  handleError(error: any) {
    if (this.isInvalidPipeArgument(error)) {
      this.handlePipeError(error);
      return;
    }

    // Suppress known @angular/flex-layout bug: DefaultClassDirective crashes
    // when NgClass._toggleClass tries to call classList.addClass on an element
    // whose nativeElement is undefined. This is non-fatal — swallow it so
    // Angular doesn't destroy the component tree.
    if (error?.message?.includes('addClass') && error?.stack?.includes('flex-layout')) {
      return;
    }

    super.handleError(error);

    if (environment.logRocket?.enabled && typeof LogRocket.captureException === 'function') {
      LogRocket.captureException(error);
    }
  }

  private isInvalidPipeArgument(error: any): boolean {
    const message = this.messageOf(error);
    return message.includes('NG02100') || message.includes('InvalidPipeArgument');
  }

  private handlePipeError(error: any): void {
    const signature = this.signatureOf(error);
    if (this.reportedPipeErrors.has(signature)) {
      // Same pipe error already reported this session — swallow the repeat so a
      // value re-rendered every tick doesn't crash the console / flood LogRocket.
      return;
    }
    this.reportedPipeErrors.add(signature);

    console.warn(
      '[NG02100] A formatting pipe received an invalid value and threw. Use ' +
      'safeDate/safeCurrency/safeNumber (they render invalid input as empty). Details:',
      error,
    );

    if (
      this.reportedPipeErrors.size <= CustomErrorHandlerService.MAX_PIPE_ERRORS &&
      environment.logRocket?.enabled &&
      typeof LogRocket.captureException === 'function'
    ) {
      LogRocket.captureException(error);
    }
  }

  private messageOf(error: any): string {
    if (!error) return '';
    if (typeof error === 'string') return error;
    return String(error.message ?? (typeof error.toString === 'function' ? error.toString() : ''));
  }

  /** Collapse the variable value out of the message so repeats dedupe together. */
  private signatureOf(error: any): string {
    return this.messageOf(error).replace(/'[^']*'/g, "'…'");
  }
}
